import React, { useEffect, useState } from "react";
import { Cog } from "lucide-react";
import { formatModelLabel } from "../lib/modelLabel";
import { EmptyState } from "./ui/empty-state";
import {
  fetchTeamAgentUsageDaily,
  emptyTotals,
  sumByModel,
  sumUsage,
  type AgentUsageDailyRow,
  type UsageTotals,
} from "../lib/agentUsage";
import { UsageCostFootnote, UsageCostText } from "./UsageCost";
import { getModelColor } from "../lib/modelColors";
import { CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Progress } from "./ui/progress";
import { Skeleton } from "./ui/skeleton";
import { rangeLabel, rangeLabelAsOf } from "../lib/dayLabel";

// ============================================================================
// Component: TopModelsUsed
//
// Replaces the old TopAgentsByTokens widget (2026-09-05) — same slot in the
// dashboard grid, but grouped by model instead of by agent/human. Real
// per-model token usage, summed across the whole team (source: see the
// 2026-10-02 note below).
//
// 2026-09-09: dropped the trailing-7-day window for the dashboard's date
// selector — took a `date` prop and filtered checkin_date to exactly that
// day. Not touched: TokenSpendOverTime, which stays a rolling trend chart
// by deliberate choice.
//
// 2026-09-17: back to a window, but now the dashboard's own selected range
// instead of a hardcoded 7 days — takes a `range` ({from, to} date-key
// strings) prop and filters checkin_date with `.gte`/`.lte`, same change
// made to TopProjects/AgentsCostPanel. Picking a single day still collapses
// from === to, so this behaves exactly as the single-day version did before.
//
// 2026-09-29: repointed from a raw `.from('agent_checkin')` select to the
// get_team_agent_checkin_aggregates RPC. agent_checkin's SELECT RLS became
// admin/manager-only that same day (20260928201832_add_pod_managers_table.sql,
// for the three-tier permission model), which silently zeroed this widget
// out for plain members -- the raw table query has no policy left that
// grants them anything. The RPC is a SECURITY DEFINER function scoped to one
// team (verifies the caller is a member of it) that strips person-
// identifying columns, so the "not admin-gated" call below still holds:
// members get the real aggregate, just not the raw per-agent rows admins/
// managers get elsewhere. See that RPC's migration
// (20260929140000_add_team_agent_checkin_aggregates_rpc.sql) for the full
// writeup.
//
// NOT admin-gated, unlike TopAgentsByTokens/TokenSpendOverTime/
// AgentsCostPanel. Those are gated because ranking by agent or human
// attributes usage to a specific person. This ranks by MODEL — it reveals
// nothing about who used what, so the privacy rationale for gating doesn't
// apply here.
//
// 2026-09-09: each row's bar is now colored per-model via
// src/lib/modelColors.ts's getModelColor — the same deterministic
// model->color mapping used by TokenSpendOverTime/TopProjects, so a given
// model reads as the same color across every dashboard card, not just a
// flat bg-primary bar.
//
// 2026-10-02 (Step 6 of docs/TOKEN_COST_ACCURACY_PLAN.md): tokens and $ now
// come from get_team_agent_usage_daily via src/lib/agentUsage.ts instead of
// get_team_agent_checkin_aggregates + a client-side rate table. Same
// any-member exposure as before. Tokens include cache read/write; $ is a
// LIST-PRICE ESTIMATE priced server-side at ingest. Fallback-priced and
// unpriced usage are marked (UsageCostText) rather than shown as $0.
// ============================================================================

interface TopModelsUsedProps {
  teamId: string;
  range: { from: string; to: string }; // YYYY-MM-DD, the dashboard's selected range
  // Pod filter (NewDashboard.tsx's pod dropdown, admin/manager only). Only
  // ever populated for admin/manager callers -- the RPC masks agent_id to
  // null for plain members, so this stays undefined for them and the widget
  // is unfiltered, same as before the pod filter existed.
  agentIds?: string[];
}

interface ModelRow {
  model: string;
  label: string;
  totalTokens: number;
  usage: UsageTotals;
}

const tokenFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const TopModelsUsed: React.FC<TopModelsUsedProps> = ({ teamId, range, agentIds }) => {
  const [rows, setRows] = useState<ModelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState<UsageTotals>(emptyTotals());

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      // usage_date (team-local), the usage equivalent of checkin_date —
      // the same "selected range" every other dashboard card keys off.
      let usageRows: AgentUsageDailyRow[];
      try {
        usageRows = await fetchTeamAgentUsageDaily(
          teamId,
          range.from,
          range.to,
          agentIds ? { agentIds } : undefined,
        );
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load top models:", err);
        setError("Could not load top models used.");
        setLoading(false);
        return;
      }

      if (cancelled) return;

      const ranked: ModelRow[] = Array.from(sumByModel(usageRows).entries())
        .map(([model, usage]) => ({
          model,
          label:
            model === "unknown" ? "Unknown model" : formatModelLabel(model),
          totalTokens: usage.totalTokens,
          usage,
        }))
        .sort((a, b) => b.totalTokens - a.totalTokens)
        .slice(0, 5);

      setTotals(sumUsage(usageRows));
      setRows(ranked);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
    // agentIds is a new array each render (NewDashboard derives it from
    // state), so depend on its joined contents rather than identity —
    // otherwise this would refetch every render once a pod is selected.
  }, [teamId, range.from, range.to, agentIds?.join(",")]);

  const max = Math.max(...rows.map((r) => r.totalTokens), 1);

  return (
    <div className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="text-base">Top models used</CardTitle>
        <CardDescription>Ranked by tokens (incl. cache) — {rangeLabel(range.from, range.to)}</CardDescription>
      </CardHeader>
      <div className="border-t border-border" />
      <CardContent className="flex flex-1 flex-col space-y-4 pt-4">
        {loading ? (
          <>
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex flex-wrap items-baseline justify-between gap-1">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-4 w-full rounded-full" />
              </div>
            ))}
          </>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Cog}
            message={`No connector activity ${rangeLabelAsOf(range.from, range.to)}.`}
          />
        ) : (
          rows.map((row) => (
            <div key={row.model} className="space-y-1.5">
              <div className="flex flex-wrap items-baseline justify-between gap-1 text-sm">
                <span className="text-foreground">{row.label}</span>
                <span className="text-xs text-muted-foreground">
                  {tokenFormatter.format(row.totalTokens)} tokens
                  {row.usage.eventCount > 0 && (
                    <>
                      {" · "}
                      <UsageCostText totals={row.usage} />
                    </>
                  )}
                </span>
              </div>
              <Progress
                value={(row.totalTokens / max) * 100}
                indicatorColor={getModelColor(row.model)}
              />
            </div>
          ))
        )}
        {!loading && !error && rows.length > 0 && <UsageCostFootnote totals={totals} />}
      </CardContent>
    </div>
  );
};

export default TopModelsUsed;
